import { LanguagesEnum } from '@/api/enums';
import { equal } from '@/api/helpers';
import {
  DashboardFilterType,
  DashboardFilterValue,
} from '@/client/reports/types';
import {
  SecurityCultureSurvey,
  SurveyScheduleStatusEnum,
  SurveyStatus,
} from '@/client/security-culture/types';
import { FilterSelector } from '@/components/campaigns/analytics/FilterSelector';
import { useSurveys } from '@/hooks/query/security-culture.hooks';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import i18n from '@/i18n';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { DateFormats } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { AppChip } from '@/ui/chip';
import { FlexContainer } from '@/ui/styled-ui';
import { formatDate } from '@/utils/date/date.helper';
import { BarIcon } from '@/utils/helpers';
import { PostHogFeatureFlags } from '@/utils/posthog/posthog.events';
import { t } from 'i18next';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const StyledDate = styled.span`
  color: var(--gray-darker);
  margin-left: 4px;
`;

type SCFiltersProps = {
  selectedSurveys?: SecurityCultureSurvey[];
  urlSurveyIds?: string[];
  filters?: DashboardFilterValue;
  setFilters?: (filters: DashboardFilterValue) => void;
  setSelectedSurveys: (surveys: SecurityCultureSurvey[] | undefined) => void;
};

export const SCFilters: React.FC<SCFiltersProps> = ({
  selectedSurveys,
  urlSurveyIds,
  filters,
  setFilters,
  setSelectedSurveys,
}) => {
  const currentAccount = useAppSelector(selectCurrentAccount);
  const user = useAppSelector(selectCurrentUser);
  const [showFilter, setShowFilter] = useState(false);
  const { accountCanUseFeature } = useFeatureFlag();
  const hasSelfServiceEnabled = accountCanUseFeature(
    PostHogFeatureFlags.SIMPLIFIED_SC_SURVEY_PLANNER,
    currentAccount?.featureFlagAbility ?? [],
  );

  const surveyStatusFilters =
    user?.account.isSystem || hasSelfServiceEnabled
      ? []
      : [equal('status', SurveyStatus.PUBLISHED)];

  const { surveys, isLoading: areSurveysLoading } = useSurveys(
    {
      take: 200,
      skip: 0,
      filters: surveyStatusFilters,
    },
    currentAccount?.id,
  );

  const {
    surveys: defaultSurveys,
    isFetching: isDefaultSurveyFetching,
    isError: isDefaultSurveyError,
  } = useSurveys(
    {
      take: 1,
      skip: 0,
      defaultCandidate: true,
      filters: surveyStatusFilters,
      enabled: !urlSurveyIds?.length,
    },
    currentAccount?.id,
  );

  useEffect(() => {
    if (!surveys?.result?.length) return;
    if (selectedSurveys !== undefined) return;

    if (urlSurveyIds?.length) {
      const matchedSurveys = urlSurveyIds.map((id) =>
        surveys.result?.find((survey) => survey.id === id),
      );
      if (matchedSurveys?.length) {
        setSelectedSurveys(
          matchedSurveys.filter((survey): survey is SecurityCultureSurvey =>
            Boolean(survey),
          ),
        );
        return;
      }
    }

    const filterableSurveys = surveys.result.filter(
      (survey) => survey?.quiz || (surveys?.count === 1 && survey?.isDemo),
    );

    if (!filterableSurveys.length && surveys?.result?.length) {
      setSelectedSurveys(surveys.result.filter((survey) => !survey?.isDemo));
      return;
    }

    if (isDefaultSurveyFetching) return;

    if (isDefaultSurveyError) {
      setSelectedSurveys(filterableSurveys);
      return;
    }

    const singleDemoSurvey =
      surveys.count === 1 && surveys.result[0]?.isDemo
        ? surveys.result[0]
        : undefined;
    const defaultSurvey =
      filterableSurveys.find(
        (survey) => survey.id === defaultSurveys?.result?.[0]?.id,
      ) ?? singleDemoSurvey;
    setSelectedSurveys(defaultSurvey ? [defaultSurvey] : []);
  }, [
    surveys?.result,
    defaultSurveys?.result,
    isDefaultSurveyFetching,
    isDefaultSurveyError,
    selectedSurveys,
    urlSurveyIds,
  ]);

  useEffect(() => {
    if (selectedSurveys?.length) {
      const filteredSurveys = surveys?.result?.filter((survey) =>
        selectedSurveys?.some(
          (selectedSurvey) => selectedSurvey.id === survey.id,
        ),
      );

      setSelectedSurveys(filteredSurveys?.length ? filteredSurveys : undefined);
    }
  }, [surveys?.result]);

  const filterLabel = {
    all_data: t('phishing.analytics.filterData.all_data'),
    managers: t('phishing.analytics.filterData.managers', {
      count: filters?.managers?.length || 0,
    }),
    departments: t('phishing.analytics.filterData.departments', {
      count: filters?.departments?.length || 0,
    }),
    branches: t('phishing.analytics.filterData.branches', {
      count: filters?.branches?.length || 0,
    }),
    groups: t('phishing.analytics.filterData.groups', {
      count: filters?.groups?.length || 0,
    }),
  };

  const getSurveyDisplayDate = (survey: SecurityCultureSurvey) => {
    if (
      survey?.scheduleStatus === SurveyScheduleStatusEnum.WAITING ||
      survey?.scheduleStatus === SurveyScheduleStatusEnum.SCHEDULED
    ) {
      return survey?.scheduledFor || survey?.date;
    }

    return survey?.date;
  };

  const itemTemplate = (
    option: SecurityCultureSurvey & {
      isLastMeasurement?: boolean;
      isChip?: boolean;
    },
  ) => {
    const optionTarget = Object.values(option?.targets || []).flat();
    const optionWrapperId = `sc-survey-option-${option.id}`;
    const displayDate = getSurveyDisplayDate(option);

    const isNotFilterable = !option?.quiz;

    return (
      <>
        <div
          id={optionWrapperId}
          style={{ display: 'flex', pointerEvents: 'auto' }}
          className={isNotFilterable ? 'p-disabled' : ''}
        >
          <FlexContainer justify="space-between">
            <span>
              {option?.isLastMeasurement ? (
                <>
                  <span>{optionTarget.map(({ name }) => name).join(', ')}</span>
                  , {t('security.culture.last.measurement')}
                  <StyledDate>
                    {`| ${formatDate(
                      displayDate,
                      DateFormats.DATEONLY,
                      i18n.language === LanguagesEnum.DK
                        ? i18n.language
                        : LanguagesEnum.EN,
                    )}`}
                  </StyledDate>
                </>
              ) : (
                <>
                  {optionTarget.length > 0 && (
                    <span>
                      {`${optionTarget.map(({ name }) => name).join(', ')}, `}
                    </span>
                  )}
                  <span>
                    {formatDate(
                      displayDate,
                      DateFormats.DATEONLY,
                      i18n.language === LanguagesEnum.DK
                        ? i18n.language
                        : LanguagesEnum.EN,
                    )}
                  </span>
                </>
              )}
            </span>

            {user?.account.isSystem &&
              !currentAccount?.isSystem &&
              !option?.isChip && (
                <>
                  {option?.status === SurveyStatus.PUBLISHED ? (
                    <AppChip label={t('generic.published')} type="primary" />
                  ) : (
                    <AppChip label={t('generic.draft')} type="secondary" />
                  )}
                </>
              )}
          </FlexContainer>
        </div>
        {isNotFilterable && !option?.isChip && (
          <Tooltip
            target={`#${optionWrapperId}`}
            content={t('security.culture.filters.nonFilterable.optionTooltip')}
            position="top"
          />
        )}
      </>
    );
  };

  const onPillRemove = (item: any) => {
    const newValue = selectedSurveys?.filter((c) => c.id !== item.id);
    setSelectedSurveys(newValue || []);
  };

  const selectedItemTemplate = (item: any) => {
    if (!item) {
      return null;
    }
    const tokenId = `sc-selected-survey-${item.id}`;
    const isNotFilterable = !item?.quiz;
    const labelValue = itemTemplate({ ...item, isChip: true });

    return (
      <>
        <div className="p-multiselect-token" key={item?.id} id={tokenId}>
          <div className="p-multiselect-token-label">{labelValue}</div>
          <button
            aria-label={t('generic.remove')}
            className="p-multiselect-token-icon pi pi-times"
            onClick={() => onPillRemove(item)}
          />
        </div>
        {isNotFilterable && (
          <Tooltip
            target={`#${tokenId}`}
            content={t('security.culture.filters.nonFilterable.optionTooltip')}
            position="top"
          />
        )}
      </>
    );
  };

  const filterButtonWrapperId = 'sc-filter-button-wrapper';
  const isFilterDisabled = !!selectedSurveys?.find((survey) => !survey?.quiz);

  return (
    <FlexContainer
      justify="space-between"
      align="flex-end"
      gap={24}
      className="mt-4"
    >
      <div className="p-inputgroup flex-1">
        <span className="p-inputgroup-addon white">
          <i className="pi pi-calendar" />
        </span>
        <MultiSelect
          className="white"
          id="sc-period-selector"
          key={selectedSurveys?.map((survey) => survey.id).join(',')}
          value={selectedSurveys}
          options={surveys?.result.filter((survey) =>
            surveys?.count > 1 ? !survey.isDemo : true,
          )}
          optionLabel="displayText"
          itemTemplate={itemTemplate}
          panelClassName="sc-filters-panel"
          selectedItemTemplate={selectedItemTemplate}
          display="chip"
          onChange={(e: MultiSelectChangeEvent) =>
            setSelectedSurveys(e.value || [])
          }
          dropdownIcon={
            areSurveysLoading ? 'pi pi-spinner pi-spin' : 'pi pi-chevron-down'
          }
          disabled={areSurveysLoading}
          placeholder={t('security.culture.filters.placeholder')}
        />
      </div>
      <>
        <div
          id={filterButtonWrapperId}
          style={{ display: 'inline-flex', width: 'fit-content' }}
        >
          <AppButton
            label={t('phishing.analytics.filterData.prefix', {
              data: filterLabel[filters?.type || DashboardFilterType.ALL_DATA],
            })}
            type="outlined"
            severity="secondary"
            icon={<BarIcon />}
            iconPos="left"
            onClick={() => setShowFilter(!showFilter)}
            isDisabled={isFilterDisabled || areSurveysLoading}
          />
        </div>
        {isFilterDisabled && (
          <Tooltip
            target={`#${filterButtonWrapperId}`}
            content={t('security.culture.filters.nonFilterable.tooltip')}
            position="top"
          />
        )}
      </>
      {showFilter && (
        <FilterSelector
          key={`filter-${JSON.stringify(filters)}`}
          onSubmit={(data) => {
            setFilters && setFilters(data);
            setShowFilter(false);
          }}
          initialFilter={filters}
          onHide={() => setShowFilter(false)}
          visible={showFilter}
        />
      )}
    </FlexContainer>
  );
};
